/**
 * Leveling Item Card Component
 * 
 * Compact card for displaying items in the leveling grid.
 * Supports drag-and-drop for realigning items.
 */

import { useState } from 'react';
import {
  GripVertical,
  AlertTriangle,
  Edit2,
  CheckCircle,
  Clock,
  Eye,
} from 'lucide-react';
import type { Candidate } from '../../../types/prioritization/lifecycle';
import { useAltitudeText } from '../../../hooks/prioritization/useAltitudeText';
import { 
  ALTITUDE_LIFECYCLE_LABELS, 
  ALTITUDE_LIFECYCLE_COLORS 
} from '../../../types/prioritization/leveling';

// ============================================================================
// Types
// ============================================================================

interface LevelingItemCardProps {
  item: Candidate;
  /** Whether the card is being dragged */
  isDragging?: boolean;
  /** Whether this item has constraint violations */
  hasViolations?: boolean;
  /** Violation count for badge */
  violationCount?: number;
  /** Callback when item is clicked */
  onClick?: () => void;
  /** Callback for quick edit */
  onQuickEdit?: () => void;
  /** Whether to show lifecycle state badge */
  showLifecycleState?: boolean;
  /** Compact mode for smaller display */
  compact?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function LevelingItemCard({
  item,
  isDragging = false,
  hasViolations = false,
  violationCount = 0,
  onClick,
  onQuickEdit,
  showLifecycleState = true,
  compact = false,
}: LevelingItemCardProps) {
  const altitudeText = useAltitudeText();
  // A legacy string altitude (feature, epic, ...) reads as its number first: "Altitude 3 · Feature".
  const altitude = item.canonicalAltitude || item.altitude;
  const lifecycleState = item.altitudeLifecycleState || 'proposed';
  
  const lifecycleColors = ALTITUDE_LIFECYCLE_COLORS[lifecycleState];
  
  return (
    <div
      className={`
        group relative bg-[var(--bg-raised)] border  transition-all
        ${isDragging 
          ? 'shadow-lg border-primary-400 ring-2 ring-primary-200' 
          : hasViolations
          ? 'border-[var(--mustard)] hover:border-[var(--mustard)]'
          : 'border-[var(--rule)] hover:border-primary-300'
        }
        ${compact ? 'p-2' : 'p-3'}
        ${onClick ? 'cursor-pointer' : ''}
      `}
      onClick={onClick}
    >
      {/* Drag handle (if parent provides drag functionality) */}
      <div className="absolute left-0 top-0 bottom-0 w-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
        <GripVertical className="h-4 w-4 text-[var(--text-ter)]" />
      </div>
      
      <div className={compact ? 'pl-4' : 'pl-5'}>
        {/* Title row */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h4 className={`font-medium text-[var(--text-pri)] truncate ${compact ? 'text-sm' : ''}`}>
              {item.title}
            </h4>
            
            {!compact && item.description && (
              <p className="text-sm text-[var(--text-ter)] truncate mt-0.5">
                {item.description}
              </p>
            )}
          </div>
          
          {/* Violation badge */}
          {hasViolations && violationCount > 0 && (
            <div className="flex-shrink-0">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded text-xs">
                <AlertTriangle className="h-3 w-3" />
                {violationCount}
              </span>
            </div>
          )}
        </div>
        
        {/* Metadata row */}
        <div className="flex items-center gap-2 mt-2">
          {/* Altitude badge */}
          {altitude && (
            <span className="px-2 py-0.5 bg-[var(--hover-tint)] text-[var(--text-sec)] rounded text-xs">
              {altitudeText.format(altitude, item.itemCategory) ?? String(altitude)}
            </span>
          )}
          
          {/* Lifecycle state badge */}
          {showLifecycleState && (
            <span className={`
              inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs
              ${lifecycleColors.bg} ${lifecycleColors.text}
            `}>
              {lifecycleState === 'accepted' && <CheckCircle className="h-3 w-3" />}
              {lifecycleState === 'under_review' && <Eye className="h-3 w-3" />}
              {lifecycleState === 'proposed' && <Clock className="h-3 w-3" />}
              {ALTITUDE_LIFECYCLE_LABELS[lifecycleState]}
            </span>
          )}
          
          {/* Quick edit button */}
          {onQuickEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onQuickEdit();
              }}
              className="ml-auto opacity-0 group-hover:opacity-100 p-1 text-[var(--text-ter)] hover:text-primary-600 transition-all"
              title="Quick edit"
            >
              <Edit2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Draggable wrapper (if using react-dnd or similar)
// ============================================================================

export interface DraggableLevelingItemProps extends LevelingItemCardProps {
  index: number;
  onDragStart?: (item: Candidate, index: number) => void;
  onDragEnd?: () => void;
}

export function DraggableLevelingItem({
  item,
  index,
  onDragStart,
  onDragEnd,
  ...cardProps
}: DraggableLevelingItemProps) {
  const [isDragging, setIsDragging] = useState(false);
  
  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.setData('application/json', JSON.stringify({
      itemId: item.id,
      sourceIndex: index,
      sourceAltitude: item.canonicalAltitude || item.altitude,
      sourceCandidateType: item.itemCategory,
    }));
    e.dataTransfer.effectAllowed = 'move';
    onDragStart?.(item, index);
  };
  
  const handleDragEnd = () => {
    setIsDragging(false);
    onDragEnd?.();
  };
  
  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <LevelingItemCard
        item={item}
        isDragging={isDragging}
        {...cardProps}
      />
    </div>
  );
}

export default LevelingItemCard;
